import { Megaphone, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useMaintenanceBannerViewModel } from "@/pages/dashboard/admin/useMaintenanceBannerViewModel";

export function MaintenanceBannerView() {
  const {
    bannerForm, setBannerForm, bannerEditingId, setBannerEditingId,
    emptyBannerForm, manageableBanner,
    createBannerMutation, updateBannerMutation, deleteBannerMutation,
    toDatetimeLocalET,
  } = useMaintenanceBannerViewModel();
  return (
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Megaphone className="size-4" />
                      Maintenance Banner
                    </CardTitle>
                    <CardDescription>Create or edit the system-wide maintenance banner shown to all users.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4">
                    {manageableBanner && !bannerEditingId && (
                      <div className="rounded-lg border p-3">
                        <div className="text-sm font-medium">{manageableBanner.message}</div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div>
                            {manageableBanner.maintenance_starts_at && `Maintenance starts: ${fmtDateTime(manageableBanner.maintenance_starts_at, "America/Detroit").replace("America/Detroit", "ET")}`}
                            {manageableBanner.maintenance_starts_at && manageableBanner.maintenance_ends_at && " — "}
                            {manageableBanner.maintenance_ends_at && `Maintenance ends: ${fmtDateTime(manageableBanner.maintenance_ends_at, "America/Detroit").replace("America/Detroit", "ET")}`}
                            {!manageableBanner.maintenance_starts_at && !manageableBanner.maintenance_ends_at && "No maintenance window set"}
                          </div>
                          <div>
                            {manageableBanner.banner_starts_at && `Banner starts: ${fmtDateTime(manageableBanner.banner_starts_at, "America/Detroit").replace("America/Detroit", "ET")}`}
                            {manageableBanner.banner_starts_at && manageableBanner.banner_ends_at && " — "}
                            {manageableBanner.banner_ends_at && `Banner ends: ${fmtDateTime(manageableBanner.banner_ends_at, "America/Detroit").replace("America/Detroit", "ET")}`}
                            {!manageableBanner.banner_starts_at && !manageableBanner.banner_ends_at && "Banner visible whenever active"}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setBannerEditingId(manageableBanner.id);
                              setBannerForm({
                                message: manageableBanner.message,
                                banner_starts_at: toDatetimeLocalET(manageableBanner.banner_starts_at),
                                banner_ends_at: toDatetimeLocalET(manageableBanner.banner_ends_at),
                                maintenance_starts_at: toDatetimeLocalET(manageableBanner.maintenance_starts_at),
                                maintenance_ends_at: toDatetimeLocalET(manageableBanner.maintenance_ends_at),
                                is_active: manageableBanner.is_active,
                              });
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                            onClick={() => {
                              if (confirm("Delete this maintenance banner?")) {
                                deleteBannerMutation.mutate(manageableBanner.id);
                              }
                            }}
                            disabled={deleteBannerMutation.isPending}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    <form
                      className="grid grid-cols-1 gap-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (!bannerForm.message) return;
                        if (bannerEditingId) {
                          updateBannerMutation.mutate({
                            id: bannerEditingId,
                            payload: {
                              message: bannerForm.message,
                              banner_starts_at: bannerForm.banner_starts_at || null,
                              banner_ends_at: bannerForm.banner_ends_at || null,
                              maintenance_starts_at: bannerForm.maintenance_starts_at || null,
                              maintenance_ends_at: bannerForm.maintenance_ends_at || null,
                              is_active: bannerForm.is_active,
                            },
                          });
                        } else {
                          createBannerMutation.mutate();
                        }
                      }}
                    >
                      <Input
                        value={bannerForm.message}
                        onChange={(e) => setBannerForm({ ...bannerForm, message: e.target.value })}
                        placeholder="Maintenance message"
                      />
                      <div className="grid gap-2">
                        <p className="text-xs font-medium text-muted-foreground">Banner visibility</p>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="grid gap-1">
                            <Label className="text-xs text-muted-foreground">Banner starts at (ET)</Label>
                            <Input
                              type="datetime-local"
                              value={bannerForm.banner_starts_at}
                              onChange={(e) => setBannerForm({ ...bannerForm, banner_starts_at: e.target.value })}
                            />
                          </div>
                          <div className="grid gap-1">
                            <Label className="text-xs text-muted-foreground">Banner ends at (ET)</Label>
                            <Input
                              type="datetime-local"
                              value={bannerForm.banner_ends_at}
                              onChange={(e) => setBannerForm({ ...bannerForm, banner_ends_at: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <p className="text-xs font-medium text-muted-foreground">Maintenance window (shown to users)</p>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="grid gap-1">
                            <Label className="text-xs text-muted-foreground">Maintenance starts (ET)</Label>
                            <Input
                              type="datetime-local"
                              value={bannerForm.maintenance_starts_at}
                              onChange={(e) => setBannerForm({ ...bannerForm, maintenance_starts_at: e.target.value })}
                            />
                          </div>
                          <div className="grid gap-1">
                            <Label className="text-xs text-muted-foreground">Maintenance ends (ET)</Label>
                            <Input
                              type="datetime-local"
                              value={bannerForm.maintenance_ends_at}
                              onChange={(e) => setBannerForm({ ...bannerForm, maintenance_ends_at: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={bannerForm.is_active}
                          onCheckedChange={(checked) => setBannerForm({ ...bannerForm, is_active: checked })}
                        />
                        <span className="text-sm">Active</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button type="submit" disabled={createBannerMutation.isPending || updateBannerMutation.isPending}>
                          {bannerEditingId ? "Update Banner" : "Create Banner"}
                        </Button>
                        {bannerEditingId && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setBannerEditingId(null);
                              setBannerForm(emptyBannerForm);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                      {(createBannerMutation.isError || updateBannerMutation.isError) && (
                        <div className="text-xs text-destructive">
                          {((createBannerMutation.error ?? updateBannerMutation.error) instanceof ApiError
                            ? (createBannerMutation.error ?? updateBannerMutation.error)?.message
                            : "Banner save failed.")}
                        </div>
                      )}
                    </form>
                  </CardContent>
                </Card>

    </div>
  );
}
